import React, { useContext, useEffect, useState } from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../../state/app/index';
import EditorButton from './EditorButton';
import HeadingButtons from './HeadingButtons';
import FontFamilySelector from './FontFamilySelector';
import FontSizeSelector from './FontSizeSelector';
import FontSizeNudgeButtons from './FontSizeNudgeButtons';
import { dryEditorClass } from '.';
import { openLinkDialog } from './LinkDialogContent';
import NoteOverflowMenu from './NoteOverflowMenu';
import NotebookSelectorForNote from '../components/NotebookSelectorForNote';
import schema from './schema';
import constants from '.././../constants';

const { EDITOR_COMMAND_NAME_MAP } = constants;

const EDITOR_BUTTON_TOOLTIPS = {
  [EDITOR_COMMAND_NAME_MAP.undo]: 'Undo (Ctrl+Z)',
  [EDITOR_COMMAND_NAME_MAP.redo]: 'Redo (Ctrl+Y)',
  [EDITOR_COMMAND_NAME_MAP.bold]: 'Bold (Ctrl+B)',
  [EDITOR_COMMAND_NAME_MAP.italic]: 'Italic (Ctrl+I)',
  [EDITOR_COMMAND_NAME_MAP.underline]: 'Underline (Ctrl+U)',
  [EDITOR_COMMAND_NAME_MAP.strikethrough]: 'Strikethrough',
  [EDITOR_COMMAND_NAME_MAP.code]: 'Inline code',
  [EDITOR_COMMAND_NAME_MAP.superscript]: 'Superscript',
  [EDITOR_COMMAND_NAME_MAP.subscript]: 'Subscript',
  [EDITOR_COMMAND_NAME_MAP.highlight]: 'Highlight text',
  [EDITOR_COMMAND_NAME_MAP.horizontal_rule]: 'Horizontal rule',
  [EDITOR_COMMAND_NAME_MAP.checkbox]: 'Checkbox',
  [EDITOR_COMMAND_NAME_MAP.check_list]: 'Checklist',
  [EDITOR_COMMAND_NAME_MAP.code_block]: 'Code block',
  [EDITOR_COMMAND_NAME_MAP.clear_all]: 'Clear all formatting',
  [EDITOR_COMMAND_NAME_MAP.unordered_list]: 'Bulleted list',
  [EDITOR_COMMAND_NAME_MAP.ordered_list]: 'Numbered list',
  [EDITOR_COMMAND_NAME_MAP.indent_list]: 'Increase indent (Tab)',
  [EDITOR_COMMAND_NAME_MAP.outdent_list]: 'Decrease indent (Shift+Tab)',
  [EDITOR_COMMAND_NAME_MAP.font_color]: 'Text color',
  [EDITOR_COMMAND_NAME_MAP.background_color]: 'Highlight color',
  [EDITOR_COMMAND_NAME_MAP.link]: 'Link (Ctrl+K)',
  [EDITOR_COMMAND_NAME_MAP.attach_file]: 'Attach file',
  [EDITOR_COMMAND_NAME_MAP.toggle_attachments]: 'Toggle attachments',
};

const Separator = () => <div className="toolbar-separator" aria-hidden="true" />;

const NoteEditorToolbar = (props) => {
  const { dropzoneReference, viewOnly, viewMode, onSetViewMode } = props;
  const globalServices = useContext(GlobalStateContext);
  const [, sendAppState] = useActor(globalServices.appState);
  const [, sendModalState] = useActor(globalServices.modalState);
  const [isLinkActive, setIsLinkActive] = useState(false);
  const [activeTab, setActiveTab] = useState('format');

  useEffect(() => {
    const anchorMark = schema.marks.anchor;
    dryEditorClass.registerTransactionHandler({
      name: 'linkActiveState',
      method: (state) => {
        const { selection } = state;
        const { $from, $to, empty } = selection;
        if (empty) {
          setIsLinkActive($from.marks().some(m => m.type === anchorMark));
        } else {
          setIsLinkActive(state.doc.rangeHasMark($from.pos, $to.pos, anchorMark));
        }
      },
    });
    return () => {
      dryEditorClass.registeredTransactionHandlers.delete('linkActiveState');
    };
  }, []);

  if (viewOnly) {
    return null;
  }

  return (
    <header id="mytoolbar" className="note-editor-toolbar">

      {/* Left column: notebook selector */}
      <div className="toolbar-notebook-col">
        <NotebookSelectorForNote />
      </div>

      {/* Right column: two-row toolbar */}
      <div className="toolbar-main">

      {/* Row 1: Tab bar + history + search */}
      <div className="toolbar-top">
        <div className="toolbar-tabs">
          <button
            className={`toolbar-tab${activeTab === 'format' ? ' active' : ''}`}
            onClick={() => setActiveTab('format')}
            type="button"
          >
            <span className="material-icons toolbar-tab__icon">text_format</span>
            Format
          </button>
          <button
            className={`toolbar-tab${activeTab === 'insert' ? ' active' : ''}`}
            onClick={() => setActiveTab('insert')}
            type="button"
          >
            <span className="material-icons toolbar-tab__icon">add_box</span>
            Insert
          </button>
          <button
            className={`toolbar-tab${activeTab === 'layout' ? ' active' : ''}`}
            onClick={() => setActiveTab('layout')}
            type="button"
          >
            <span className="material-icons toolbar-tab__icon">view_compact</span>
            Layout
          </button>
        </div>

        <div className="toolbar-top__divider" aria-hidden="true" />

        <EditorButton
          commandName={EDITOR_COMMAND_NAME_MAP.undo}
          title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.undo]}
        />
        <EditorButton
          commandName={EDITOR_COMMAND_NAME_MAP.redo}
          title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.redo]}
        />

        <div className="toolbar-top__spacer" />

        <button className="toolbar-search-btn" type="button" aria-label="Search formatting">
          <span className="material-icons toolbar-search-btn__icon">search</span>
          <span className="toolbar-search-btn__label">Search formatting</span>
          <kbd className="toolbar-search-btn__kbd">⌘K</kbd>
        </button>

        <NoteOverflowMenu viewMode={viewMode} onSetViewMode={onSetViewMode} />
      </div>

      {/* Row 2: Tab content */}
      <nav className="toolbar-options">

        {activeTab === 'format' && <>
          <HeadingButtons />
          <Separator />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.bold} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.bold]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.italic} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.italic]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.underline} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.underline]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.strikethrough} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.strikethrough]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.code} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.code]} />
          <Separator />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.font_color} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.font_color]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.background_color} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.background_color]} />
          <Separator />
          <FontSizeNudgeButtons />
        </>}

        {activeTab === 'insert' && <>
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.unordered_list} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.unordered_list]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.ordered_list} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.ordered_list]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.check_list} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.check_list]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.checkbox} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.checkbox]} />
          <Separator />
          <EditorButton
            commandName={EDITOR_COMMAND_NAME_MAP.link}
            title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.link]}
            isActive={isLinkActive}
            overrideClickEvent={(e) => {
              e.preventDefault();
              openLinkDialog(sendModalState);
            }}
          />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.code_block} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.code_block]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.horizontal_rule} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.horizontal_rule]} />
          <Separator />
          <EditorButton
            commandName={EDITOR_COMMAND_NAME_MAP.attach_file}
            title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.attach_file]}
            overrideClickEvent={() => {
              if (dropzoneReference && dropzoneReference.current) {
                dropzoneReference.current.open();
              }
            }}
          />
        </>}

        {activeTab === 'layout' && <>
          <FontFamilySelector />
          <FontSizeSelector />
          <Separator />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.indent_list} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.indent_list]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.outdent_list} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.outdent_list]} />
          <Separator />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.superscript} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.superscript]} />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.subscript} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.subscript]} />
          <Separator />
          <EditorButton commandName={EDITOR_COMMAND_NAME_MAP.clear_all} title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.clear_all]} />
          <EditorButton
            commandName={EDITOR_COMMAND_NAME_MAP.toggle_attachments}
            title={EDITOR_BUTTON_TOOLTIPS[EDITOR_COMMAND_NAME_MAP.toggle_attachments]}
            overrideClickEvent={() => {
              sendAppState({ type: 'toggleAttachmentsSection' });
            }}
          />
        </>}

      </nav>

      </div>{/* end toolbar-main */}
    </header>
  );
};

export default NoteEditorToolbar;
